/**
 * HomePage Component
 * Main landing page displaying dashboard cards, applications, and obligo information
 * Refactored following Single Responsibility Principle and clean code standards
 */

import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Container, Box, CircularProgress, Menu, MenuItem, ListItemIcon } from "@mui/material";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources } from "../authConfig";
import { useUser } from "../contexts/UserContext";
import { useAllBranches } from "../contexts/BranchesContext";
import { useTransactionsType } from "../contexts/TransactionsTypeContext";
import { useHomePageData } from "./HomePage/hooks/useHomePageData";
import { useDashboardCards } from "./HomePage/hooks/useDashboardCards";
import { getTimeBasedGreeting, navigateToExternalApp, handleUserLogout, canViewObligoSection } from "./HomePage/utils";
import { OBLIGO_TABS } from "./HomePage/constants";
import HomePageHeader from "./HomePage/components/HomePageHeader";
import AppsGrid from "./HomePage/components/AppsGrid";
import ObligoSection from "./HomePage/components/ObligoSection";

const HomePage = () => {
  // MSAL and authentication
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  // Context hooks
  const { user, setUser } = useUser();
  const { setAllBranches } = useAllBranches();
  const { setTransactionsType } = useTransactionsType();

  // Fetch hook
  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  // Local state
  const [greeting, setGreeting] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(OBLIGO_TABS.EXCEPTIONS);

  // Custom hooks for data management
  const { pageData, loading, obligoDetails, ordersLog } = useHomePageData(execute, user);
  const dashboardCards = useDashboardCards(activeAccount, user, pageData, loading);
  
  useEffect(() => {
    console.log('User Access Rights:', user?.accessRights);
    console.log('Dashboard Cards:', dashboardCards);
    console.log('External cards:', dashboardCards.filter(c => c.isExternal));
    console.log('Has ImportManagementPage:', user?.accessRights?.ImportManagementPage);
    console.log('Has emailTrackingPage:', user?.accessRights?.emailTrackingPage);
  }, [dashboardCards, user]);

  // Set greeting based on time of day
  useEffect(() => {
    setGreeting(getTimeBasedGreeting());
  }, []);

  /**
   * Handles tab change in obligo section
   * @param {Event} event - Change event
   * @param {number} newValue - New tab value
   */
  const handleTabChange = (event, newValue) => {
    if (newValue !== null) {
      setTabValue(newValue);
    }
  };

  /**
   * Handles user logout with cleanup
   */
  const onLogout = () => {
    handleUserLogout(instance, setUser, setAllBranches, setTransactionsType);
  };

const handleExternalClick = async (url, card) => {
  if (card?.id === 'research-monitor') {
    try {
      const isAdmin = user?.accessRights?.admin === true;

      const researchMonitorRole = user?.roles?.reduce((acc, r) => {
        if (r.ResearchMonitorRole === 'manager') return 'manager';
        if (r.ResearchMonitorRole === 'employee' && acc !== 'manager') return 'employee';
        return acc;
      }, 'employee');

      const finalRole = isAdmin ? 'manager' : researchMonitorRole;
      const email = activeAccount?.username || '';

      const cachedToken = instance
        .getAllAccounts()[0]
        ?.idTokenClaims ? null : null; // placeholder

      const tokenResponse = await instance.acquireTokenSilent({
        scopes: protectedResources.apiScopes.scopes.read,
        account: activeAccount,
        forceRefresh: false,        
        cacheLookupPolicy: 0,       
      });

      const params = new URLSearchParams({
        token: tokenResponse.accessToken,
        ResearchMonitorRole: finalRole,
        email,
      });

      if (isAdmin) params.append('admin', 'true');

      const finalUrl = `${url}?${params.toString()}`;
      console.log(' Redirecting | role:', finalRole, '| admin:', isAdmin, '| email:', email);

      window.location.href = finalUrl;

    } catch (error) {
      console.warn(' Silent token failed, trying popup:', error.message);

      try {
        const tokenResponse = await instance.acquireTokenPopup({
          scopes: protectedResources.apiScopes.scopes.read,
          account: activeAccount,
        });

        const isAdmin = user?.accessRights?.admin === true;
        const finalRole = isAdmin ? 'manager' : 'employee';
        const email = activeAccount?.username || '';

        const params = new URLSearchParams({
          token: tokenResponse.accessToken,
          ResearchMonitorRole: finalRole,
          email,
        });

        if (isAdmin) params.append('admin', 'true');
        window.location.href = `${url}?${params.toString()}`;

      } catch (popupError) {
        console.error(' Popup also failed:', popupError.message);
        window.open(url, '_blank');
      }
    }
    return;
  }

  if (card?.id === 'fuel-control') {
    try {
      const result = await instance.acquireTokenSilent({
        scopes: protectedResources.apiScopes.scopes.read,
        account: activeAccount,
      });

      const email = activeAccount?.username || '';

      const params = new URLSearchParams({
        token: result.accessToken,
        email: email,
      });

      const finalUrl = `${url}?${params.toString()}`;
      window.location.href = finalUrl;

    } catch (error) {
      console.error('Failed to acquire token for Fuel Control:', error);
      window.location.href = url;
    }
    return;
  }

  navigateToExternalApp(url);
};

  // All applications now come from routes.js with consistent permission handling
  const hasApplications = dashboardCards.length > 0;
  const shouldShowObligoSection = activeAccount && canViewObligoSection(user?.accessRights);

  // Loading state - show spinner while user data is being fetched
  if (user.fullName === undefined) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FAFC",
        pt: { xs: 8, sm: 9 },
        pb: { xs: 4, sm: 6 },
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <HomePageHeader
          greeting={greeting}
          userName={activeAccount ? user.fullName : null}
          hasApplications={hasApplications}
        />

        {/* User Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem onClick={onLogout}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            יציאה
          </MenuItem>
        </Menu>

        {/* Applications Grid */}
        <AppsGrid cards={dashboardCards} onExternalAppClick={handleExternalClick} />

        {/* Obligo Exceptions Section */}
        {shouldShowObligoSection && (
          <ObligoSection
            tabValue={tabValue}
            onTabChange={handleTabChange}
            obligoDetails={obligoDetails}
            ordersLog={ordersLog}
            isLoading={loading.obligoExceptions}
          />
        )}

        {/* Bottom Spacing */}
        <Box sx={{ height: { xs: 20, sm: 40 } }} />
      </Container>
    </Box>
  );
};

export default HomePage;
